import React from 'react';
import { Table, Button, Space, Tag } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';

interface StudentData {
  id: string;
  name: string;
  email: string;
  grade: string;
  status: 'active' | 'inactive';
  registrationDate: string;
  academyCode: string;
  birthDate: string;
}

interface StudentTableProps {
  data: StudentData[];
  showActions?: boolean;
  showCheckbox?: boolean;
  onDelete?: (studentId: string) => void;
  onEdit?: (studentId: string) => void;
  onSelect?: (selectedStudents: StudentData[]) => void;
  selectable?: boolean;
  showSelectedColumns?: boolean;
}

const StudentTable: React.FC<StudentTableProps> = ({
  data,
  showActions = true,
  showCheckbox = false,
  onDelete,
  onEdit,
  onSelect,
  selectable = false,
  showSelectedColumns = false
}) => {
  const totalCount = data.length;
  const descendingNo = (index: number) => {
    if (totalCount === 0) return 0;
    const value = totalCount - index;
    return value > 0 ? value : 0;
  };

  const columns = showSelectedColumns ? [
    {
      title: '순번',
      dataIndex: 'id',
      key: 'id',
      render: (_: any, __: any, index: number) => descendingNo(index),
      width: 80,
      align: 'center' as const
    },
    {
      title: '학원 코드',
      dataIndex: 'academyCode',
      key: 'academyCode',
      width: 120
    },
    {
      title: '학생명',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: '생년월일',
      dataIndex: 'birthDate',
      key: 'birthDate',
      width: 120
    }
  ] : [
    {
      title: '순번',
      dataIndex: 'id',
      key: 'id',
      render: (_: any, __: any, index: number) => descendingNo(index),
      width: 80,
      align: 'center' as const
    },
    {
      title: '학생명',
      dataIndex: 'name',
      key: 'name',
      width: 120
    },
    {
      title: '이메일',
      dataIndex: 'email',
      key: 'email',
      width: 200
    },
    {
      title: '학년',
      dataIndex: 'grade',
      key: 'grade',
      width: 100,
      align: 'center' as const
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '활성' : '비활성'}
        </Tag>
      )
    },
    {
      title: '가입일',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      width: 120,
      align: 'center' as const
    }
  ];

  // 액션 컬럼 추가
  if (showActions) {
    columns.push({
      title: '액션',
      key: 'action',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: StudentData) => (
        <Space size="small">
          {onEdit && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record.id)}
              size="small"
            />
          )}
          {onDelete && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onDelete(record.id)}
              size="small"
            >
              삭제
            </Button>
          )}
        </Space>
      )
    } as any);
  }

  return (
    <Table
      columns={columns}
      dataSource={data}
      rowKey="id"
      size="small"
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`
      }}
      rowSelection={selectable ? {
        onChange: (selectedRowKeys, selectedRows) => {
          if (onSelect) {
            onSelect(selectedRows);
          }
        }
      } : undefined}
    />
  );
};

export default StudentTable; 